import { ComponentProps, type MouseEvent, type ReactNode } from 'react';

import { Button, Flex, Focusable } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import {
  SplitButton,
  SplitButtonOptionProps,
  SplitButtonProps,
} from '../SplitButton';
import { asDialogSlot } from './composition';
import { useDialogContext } from './context';

import styles from './DialogComposite.module.css';

type ButtonBaseProps = Omit<ComponentProps<typeof Button>, 'variant'>;

const Cancel = (props: ButtonBaseProps) => {
  const { onClose, isLoading } = useDialogContext();
  const { children = 'Cancel', onClick, disabled, ...rest } = props;

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onClose();
  };

  return (
    <Button
      variant="tertiary"
      isCondensedInline
      hasNoInteractiveBackground
      onClick={handleClick}
      disabled={isLoading || disabled}
      data-track-id="button.cancel"
      data-track-action="dismiss"
      {...rest}
    >
      {children}
    </Button>
  );
};

type PrimaryProps = ButtonBaseProps & { tooltip?: string };

const Primary = ({ tooltip, ...props }: PrimaryProps) => {
  const { isLoading } = useDialogContext();
  const {
    children = 'Save',
    disabled,
    onClick,
    type,
    isVisuallyDisabled,
    'aria-disabled': ariaDisabled,
    ...rest
  } = props;
  const explainsDisabled = Boolean(tooltip) && Boolean(disabled) && !isLoading;

  const button = (
    <Button
      variant="primary"
      data-testid="primary-button"
      isLoading={isLoading}
      loadingAriaLabel="Loading"
      data-track-id="button.submit"
      data-track-action="update"
      {...rest}
      disabled={isLoading || (disabled && !explainsDisabled)}
      isVisuallyDisabled={explainsDisabled || isVisuallyDisabled}
      aria-disabled={explainsDisabled || ariaDisabled}
      type={explainsDisabled ? 'button' : type}
      onClick={explainsDisabled ? undefined : onClick}
    >
      {children}
    </Button>
  );

  if (tooltip) {
    return (
      <TooltipRoot>
        <Focusable>{button}</Focusable>
        <Tooltip>{tooltip}</Tooltip>
      </TooltipRoot>
    );
  }

  return button;
};

const Secondary = (props: ButtonBaseProps) => {
  const { isLoading } = useDialogContext();
  const { children = 'Create', disabled, ...rest } = props;

  return (
    <Button
      variant="secondary-bordered"
      data-testid="secondary-button"
      disabled={isLoading || disabled}
      isLoading={isLoading}
      loadingAriaLabel="Loading"
      data-track-id="button.secondary"
      data-track-action="update"
      {...rest}
    >
      {children}
    </Button>
  );
};

const MultiActionOption = (props: SplitButtonOptionProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return <SplitButton.Option {...rest} disabled={isLoading || disabled} />;
};

const MultiAction = (props: SplitButtonProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return (
    <SplitButton
      variant="primary"
      data-testid="multi-action-button"
      {...rest}
      disabled={isLoading || disabled}
    />
  );
};

MultiAction.Option = MultiActionOption;

const Submit = (props: PrimaryProps) => {
  return <Primary type="submit" {...props} />;
};

export type ActionsProps = {
  leading?: ReactNode;
  children?: ReactNode;
};

const Actions = ({ leading, children }: ActionsProps) => {
  if (!leading) {
    return <>{children}</>;
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap="base"
      className={styles.footer}
    >
      <span>{leading}</span>
      <Flex alignItems="center" gap="tighter">
        {children}
      </Flex>
    </Flex>
  );
};

Actions.displayName = 'DialogComposite.Actions';

asDialogSlot(Actions, 'actions');

export default Object.assign(Actions, {
  Cancel,
  Submit,
  Secondary,
  Primary,
  MultiAction,
});
